import type { Editor } from '@tiptap/react';

/**
 * Merges the attrs returned by `update` onto the task item node at `position`
 * with a `tr.setNodeMarkup` transaction, targeting the exact node position so
 * nested task items are updated unambiguously.
 *
 * Returns the node's merged attrs, or `undefined` (without dispatching) when
 * there is no node at `position`.
 */
export const updateTaskItemAttrs = ({
    editor,
    position,
    update,
}: {
    editor: Editor;
    position: number;
    update: (attrs: Record<string, unknown>) => Record<string, unknown>;
}): Record<string, unknown> | undefined => {
    const node = editor.state.doc.nodeAt(position);
    if (!node) return undefined;

    const attrs = { ...node.attrs, ...update(node.attrs) };
    editor.view.dispatch(editor.state.tr.setNodeMarkup(position, undefined, attrs));
    editor.commands.flushPendingUpdate({});

    return attrs;
};

/**
 * Sets `checked` on the task item at `position` and on every task item nested
 * beneath it, in a single transaction.
 *
 * Setting node markup preserves each node's size, so the positions collected from
 * the pre-transaction document stay valid for the whole transaction.
 */
export const setTaskItemCheckedWithSubTasks = ({
    editor,
    position,
    checked,
}: {
    editor: Editor;
    position: number;
    checked: boolean;
}): void => {
    const taskItem = editor.state.doc.nodeAt(position);
    if (!taskItem) return;

    const { tr } = editor.state;
    tr.setNodeMarkup(position, undefined, { ...taskItem.attrs, checked });

    taskItem.descendants((node, offset) => {
        if (node.type !== taskItem.type) return true;

        // `offset` is relative to the start of the task item's content.
        tr.setNodeMarkup(position + 1 + offset, undefined, { ...node.attrs, checked });
        return true;
    });

    editor.view.dispatch(tr);
};
